import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { TrendUp, TrendDown, ArrowLeft, Prohibit, CaretUp, CaretDown, Funnel } from "phosphor-react";
import { Button } from "@/components/ui/button";
import GeniLoader from "../components/GeniLoader";

// Mapping cancel reasons to user-friendly labels
const CANCEL_REASON_LABELS = {
  setup_not_triggered: "Invalidated Before Entry",
  no_entry_reach: "Did Not Reach Entry",
  skipped_execution: "Skipped Execution"
};

export default function PlannedTradesDetail() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [trades, setTrades] = useState([]);
  const [allTrades, setAllTrades] = useState([]);
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  
  // Read setup filter from URL query params
  const setupFilterFromUrl = searchParams.get('setup');
  
  // Filter and sort state
  const [directionFilter, setDirectionFilter] = useState(null); // null | 'long' | 'short'
  const [reasonFilter, setReasonFilter] = useState(null); // null | 'setup_not_triggered' | 'no_entry_reach' | 'skipped_execution'
  const [sortColumn, setSortColumn] = useState(null); // null | 'date' | 'asset'
  const [sortDirection, setSortDirection] = useState(null); // null | 'asc' | 'desc'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tradesRes, setupsRes] = await Promise.all([
        api.get('/trades'),
        api.get('/setups')
      ]);
      const allTradesData = tradesRes.data;
      setAllTrades(allTradesData);
      setSetups(setupsRes.data);
      
      // Filter to only canceled trades (planned but not triggered)
      const canceledTrades = allTradesData.filter(t => t.status === 'canceled');
      setTrades(canceledTrades);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load planned trades");
    } finally {
      setLoading(false);
    }
  };

  // Get setup name from setup_id
  const getSetupName = (setupId) => {
    if (!setupId) return null;
    if (setupId === '__no_setup__') return 'No setup';
    const setup = setups.find(s => s.id === setupId);
    return setup?.name || null;
  };

  // Filter trades by setup from URL param
  const setupFilteredTrades = useMemo(() => {
    if (!setupFilterFromUrl) return trades;
    
    if (setupFilterFromUrl === '__no_setup__') {
      return trades.filter(t => !t.setup_id);
    }
    return trades.filter(t => t.setup_id === setupFilterFromUrl);
  }, [trades, setupFilterFromUrl]);

  // Filter allTrades by setup for accurate percentage calculation
  const setupFilteredAllTrades = useMemo(() => {
    if (!setupFilterFromUrl) return allTrades;
    
    if (setupFilterFromUrl === '__no_setup__') {
      return allTrades.filter(t => !t.setup_id);
    }
    return allTrades.filter(t => t.setup_id === setupFilterFromUrl);
  }, [allTrades, setupFilterFromUrl]);

  // Clear setup filter
  const clearSetupFilter = () => {
    setSearchParams({});
  };

  // Normalize cancel reason - treat null/undefined/unknown as "skipped_execution"
  const getNormalizedReason = (trade) => {
    const reason = trade.cancel_reason;
    if (reason && ['setup_not_triggered', 'no_entry_reach', 'skipped_execution'].includes(reason)) {
      return reason;
    }
    return 'skipped_execution';
  };

  // Get human-readable reason label
  const getReasonLabel = (trade) => {
    const reason = getNormalizedReason(trade);
    return CANCEL_REASON_LABELS[reason] || "Skipped Execution";
  };

  // Cycle direction filter: null → long → short → null
  const cycleDirectionFilter = () => {
    if (directionFilter === null) setDirectionFilter('long');
    else if (directionFilter === 'long') setDirectionFilter('short');
    else setDirectionFilter(null);
  };

  // Cycle reason filter: null → no_entry_reach → setup_not_triggered → skipped_execution → null
  const cycleReasonFilter = () => {
    if (reasonFilter === null) setReasonFilter('no_entry_reach');
    else if (reasonFilter === 'no_entry_reach') setReasonFilter('setup_not_triggered');
    else if (reasonFilter === 'setup_not_triggered') setReasonFilter('skipped_execution');
    else setReasonFilter(null);
  };

  // Cycle Date sort: null → desc (recent) → asc (oldest) → null
  const cycleDateSort = () => {
    if (sortColumn !== 'date') {
      setSortColumn('date');
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Cycle Asset sort: null → asc (A-Z) → desc (Z-A) → null
  const cycleAssetSort = () => {
    if (sortColumn !== 'asset') {
      setSortColumn('asset');
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Apply filters and sorting (uses setupFilteredTrades as base)
  const filteredAndSortedTrades = useMemo(() => {
    let result = [...setupFilteredTrades];

    // Apply direction filter
    if (directionFilter) {
      result = result.filter(t => t.direction === directionFilter);
    }

    // Apply reason filter
    if (reasonFilter) {
      result = result.filter(t => getNormalizedReason(t) === reasonFilter);
    }

    // Apply sorting
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        let aVal, bVal;
        
        if (sortColumn === 'date') {
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
        } else if (sortColumn === 'asset') {
          aVal = (a.asset || '').toLowerCase();
          bVal = (b.asset || '').toLowerCase();
          if (sortDirection === 'desc') {
            return bVal.localeCompare(aVal);
          } else {
            return aVal.localeCompare(bVal);
          }
        }
        
        if (sortDirection === 'desc') {
          return bVal - aVal;
        } else {
          return aVal - bVal;
        }
      });
    } else {
      // Default sort by date descending
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return result;
  }, [setupFilteredTrades, directionFilter, reasonFilter, sortColumn, sortDirection]);

  // Calculate summary metrics (uses setup-filtered data)
  const summaryMetrics = useMemo(() => {
    const total = setupFilteredTrades.length;
    const totalAllTrades = setupFilteredAllTrades.length;
    
    // Count by reason
    const counts = {
      no_entry_reach: 0,
      setup_not_triggered: 0,
      skipped_execution: 0
    };
    
    setupFilteredTrades.forEach(trade => {
      const reason = getNormalizedReason(trade);
      counts[reason]++;
    });
    
    return {
      total,
      percentOfAll: totalAllTrades > 0 ? ((total / totalAllTrades) * 100).toFixed(1) : 0,
      didNotReachEntry: counts.no_entry_reach,
      invalidatedBeforeEntry: counts.setup_not_triggered,
      skippedExecution: counts.skipped_execution
    };
  }, [setupFilteredTrades, setupFilteredAllTrades]);

  // Column header component with filter/sort indicator
  const ColumnHeader = ({ label, isActive, activeLabel, onClick, sortDir }) => (
    <th 
      className={`p-3 cursor-pointer select-none transition-colors hover:bg-white/5 ${
        isActive ? 'text-white' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        {isActive && (
          <span className="flex items-center">
            {sortDir === 'desc' && <CaretDown size={12} weight="bold" className="text-[#FFA726]" />}
            {sortDir === 'asc' && <CaretUp size={12} weight="bold" className="text-[#FFA726]" />}
            {!sortDir && activeLabel && (
              <span className="text-[10px] px-1 py-0.5 rounded bg-[#FFA726]/20 text-[#FFA726] font-semibold ml-1">
                {activeLabel}
              </span>
            )}
          </span>
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="planned-trades-detail-page">
      <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/advanced')}
            variant="ghost"
            className="text-white/60 hover:text-white hover:bg-white/5 p-2"
            data-testid="back-to-advanced"
          >
            <ArrowLeft size={20} weight="bold" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Prohibit size={28} weight="duotone" className="text-[#FFA726]" />
              <div>
                <div className="text-white/40 text-xs font-mono uppercase tracking-wider">PLANNED TRADES</div>
                <h2 className="text-3xl font-black font-heading tracking-tight" data-testid="page-title">
                  Not Triggered
                </h2>
              </div>
            </div>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-sm px-4 py-2">
            <span className="text-white/60 text-sm font-mono">TOTAL: </span>
            <span className="text-[#FFA726] font-bold text-lg" data-testid="total-count">{setupFilteredTrades.length}</span>
          </div>
        </div>

        {/* Setup Filter Indicator */}
        {setupFilterFromUrl && (
          <div className="mb-4 flex items-center gap-2 text-white/60 text-sm" data-testid="setup-filter-indicator">
            <span>Showing planned trades for:</span>
            <span className="font-bold text-[#FFA726]">
              {getSetupName(setupFilterFromUrl) || setupFilterFromUrl}
            </span>
            <button
              onClick={clearSetupFilter}
              className="text-white/40 hover:text-white/60 ml-1 underline"
              data-testid="clear-setup-filter"
            >
              (clear)
            </button>
          </div>
        )}

        {/* Performance Summary Row */}
        {setupFilteredTrades.length > 0 && (
          <div className="mb-6" data-testid="planned-trades-summary">
            <div className="text-white/40 text-xs font-mono uppercase tracking-wider mb-3">
              Summary
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {/* Total Not Triggered */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">TRADES</div>
                  <div className="text-xl font-bold font-mono text-[#FFA726]" data-testid="summary-total">
                    {summaryMetrics.total}
                  </div>
                </div>
                
                {/* % of Total */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">% OF ALL TRADES</div>
                  <div className="text-xl font-bold font-mono text-white" data-testid="summary-percent">
                    {summaryMetrics.percentOfAll}%
                  </div>
                </div>
                
                {/* Did Not Reach Entry */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">DID NOT REACH ENTRY</div>
                  <div className="text-xl font-bold font-mono text-white" data-testid="summary-no-entry">
                    {summaryMetrics.didNotReachEntry}
                  </div>
                </div>
                
                {/* Invalidated Before Entry */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">INVALIDATED</div>
                  <div className="text-xl font-bold font-mono text-white" data-testid="summary-invalidated">
                    {summaryMetrics.invalidatedBeforeEntry}
                  </div>
                </div>
                
                {/* Skipped Execution */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">SKIPPED EXECUTION</div>
                  <div className="text-xl font-bold font-mono text-white" data-testid="summary-skipped">
                    {summaryMetrics.skippedExecution}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {setupFilteredTrades.length === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
            <div className="w-16 h-16 bg-[#FFA726]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Prohibit size={32} weight="duotone" className="text-[#FFA726]/60" />
            </div>
            <h3 className="text-xl font-bold font-heading mb-2">
              {setupFilterFromUrl ? "No Planned Trades for This Setup" : "No Planned Trades Found"}
            </h3>
            <p className="text-white/60 mb-4">
              {setupFilterFromUrl 
                ? `No trades marked as planned but not triggered for "${getSetupName(setupFilterFromUrl) || setupFilterFromUrl}".`
                : "No trades have been marked as planned but not triggered yet."}
            </p>
            {setupFilterFromUrl && (
              <Button
                onClick={clearSetupFilter}
                variant="outline"
                className="border-white/10 text-white/70 hover:bg-white/5"
                data-testid="clear-filter-empty-state"
              >
                View All Planned Trades
              </Button>
            )}
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Trade List */}
            <div className="flex-1">
              {/* Active Filters Summary */}
              {(directionFilter || reasonFilter || sortColumn) && (
                <div className="mb-3 flex items-center gap-2 text-xs">
                  <Funnel size={14} className="text-white/40" />
                  <span className="text-white/40">Active:</span>
                  {directionFilter && (
                    <span className="px-2 py-0.5 rounded bg-white/10 text-white/80">
                      {directionFilter.toUpperCase()}
                    </span>
                  )}
                  {reasonFilter && (
                    <span className="px-2 py-0.5 rounded bg-[#FFA726]/20 text-[#FFA726]">
                      {CANCEL_REASON_LABELS[reasonFilter]}
                    </span>
                  )}
                  {sortColumn && (
                    <span className="px-2 py-0.5 rounded bg-white/10 text-white/80">
                      {sortColumn.toUpperCase()} {sortDirection === 'desc' ? '↓' : '↑'}
                    </span>
                  )}
                  <button 
                    onClick={() => {
                      setDirectionFilter(null);
                      setReasonFilter(null);
                      setSortColumn(null);
                      setSortDirection(null);
                    }}
                    className="text-white/40 hover:text-white ml-2 underline"
                    data-testid="clear-all-filters"
                  >
                    Clear all
                  </button>
                </div>
              )}
              
              <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                <table className="w-full" data-testid="planned-trades-table">
                  <thead className="bg-[#0A0A0A]">
                    <tr className="text-left text-xs text-white/40 font-mono">
                      <ColumnHeader 
                        label="ASSET" 
                        isActive={sortColumn === 'asset'}
                        sortDir={sortColumn === 'asset' ? sortDirection : null}
                        onClick={cycleAssetSort}
                      />
                      <ColumnHeader 
                        label="DIRECTION" 
                        isActive={!!directionFilter}
                        activeLabel={directionFilter?.toUpperCase()}
                        onClick={cycleDirectionFilter}
                      />
                      <ColumnHeader 
                        label="REASON" 
                        isActive={!!reasonFilter}
                        activeLabel={reasonFilter ? CANCEL_REASON_LABELS[reasonFilter]?.split(' ')[0] : null}
                        onClick={cycleReasonFilter}
                      />
                      <th className="p-3">SETUP</th>
                      <ColumnHeader 
                        label="DATE" 
                        isActive={sortColumn === 'date'}
                        sortDir={sortColumn === 'date' ? sortDirection : null}
                        onClick={cycleDateSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedTrades.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-white/40">
                          No trades match current filters
                        </td>
                      </tr>
                    ) : (
                      filteredAndSortedTrades.map((trade) => (
                        <tr
                          key={trade.id}
                          data-testid={`trade-row-${trade.id}`}
                          onClick={() => setSelectedTrade(trade)}
                          className={`trade-row border-t border-white/10 cursor-pointer hover:bg-white/5 ${
                            selectedTrade?.id === trade.id ? "bg-white/5" : ""
                          }`}
                        >
                          <td className="p-3">
                            <div className="font-mono text-white font-bold">{trade.asset}</div>
                            <div className="text-xs text-white/40">{trade.timeframe || '-'}</div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {trade.direction === "long" ? (
                                <TrendUp size={16} weight="bold" className="text-[#00E676]" />
                              ) : (
                                <TrendDown size={16} weight="bold" className="text-[#FF3B30]" />
                              )}
                              <span
                                className={`font-mono text-sm uppercase ${
                                  trade.direction === "long" ? "text-[#00E676]" : "text-[#FF3B30]"
                                }`}
                              >
                                {trade.direction}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            <span className="px-2 py-0.5 text-xs font-mono rounded bg-[#FFA726]/10 text-[#FFA726]">
                              {getReasonLabel(trade)}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-white/60 text-sm">
                              {trade.setup_name || 'No setup'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-white/60 text-sm">
                              {trade.created_at && format(new Date(trade.created_at), "MMM d, yyyy")}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                
                {/* Results count */}
                {(directionFilter || reasonFilter) && (
                  <div className="p-3 border-t border-white/10 text-xs text-white/40">
                    Showing {filteredAndSortedTrades.length} of {setupFilteredTrades.length} trades
                  </div>
                )}
              </div>
            </div>

            {/* Trade Details Panel - Read Only */}
            {selectedTrade && (
              <div className="w-[400px] shrink-0">
                <div className="bg-[#121212] border border-white/10 rounded-sm p-6 sticky top-8" data-testid="trade-details-panel">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold font-heading">Trade Details</h3>
                    <Button
                      onClick={() => setSelectedTrade(null)}
                      variant="ghost"
                      size="sm"
                      className="text-white/40 hover:text-white hover:bg-white/5"
                      data-testid="close-details-panel"
                    >
                      <ArrowLeft size={16} />
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {/* Chart Image */}
                    {selectedTrade.image_data && (
                      <div className="bg-[#000000] border border-white/10 rounded-sm overflow-hidden">
                        <img 
                          src={`data:image/png;base64,${selectedTrade.image_data}`}
                          alt="Trade chart"
                          className="w-full h-auto object-contain"
                          data-testid="trade-chart-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Asset & Direction */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-black font-heading">{selectedTrade.asset}</div>
                        <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs font-bold ${
                          selectedTrade.direction === "long" 
                            ? "bg-[#00E676]/10 text-[#00E676]" 
                            : "bg-[#FF3B30]/10 text-[#FF3B30]"
                        }`}>
                          {selectedTrade.direction === "long" ? (
                            <TrendUp size={14} weight="bold" />
                          ) : (
                            <TrendDown size={14} weight="bold" />
                          )}
                          {selectedTrade.direction?.toUpperCase()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-white/40 mb-1">STATUS</div>
                        <div className="px-2 py-1 rounded bg-[#FFA726]/10 text-[#FFA726] text-sm font-bold">
                          NOT TRIGGERED
                        </div>
                      </div>
                    </div>

                    {/* Cancel Reason */}
                    <div className="bg-[#FFA726]/5 border border-[#FFA726]/20 rounded-sm p-4">
                      <div className="text-white/40 text-xs font-mono mb-2">REASON NOT TRIGGERED</div>
                      <div className="text-[#FFA726] font-bold">
                        {getReasonLabel(selectedTrade)}
                      </div>
                    </div>

                    {/* Price Levels */}
                    <div className="grid grid-cols-2 gap-4">
                      {selectedTrade.entry && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">PLANNED ENTRY</div>
                          <div className="text-white font-mono font-semibold">
                            ${selectedTrade.entry?.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedTrade.stop_loss && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">STOP LOSS</div>
                          <div className="text-[#FF3B30] font-mono font-semibold">
                            ${selectedTrade.stop_loss?.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedTrade.take_profit && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">TAKE PROFIT</div>
                          <div className="text-[#00E676] font-mono font-semibold">
                            ${selectedTrade.take_profit?.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedTrade.hand_size && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">HAND SIZE</div>
                          <div className="text-white font-mono font-semibold">
                            ${selectedTrade.hand_size?.toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Additional Info */}
                    <div className="space-y-3 pt-4 border-t border-white/10">
                      {selectedTrade.setup_name && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Setup</span>
                          <span className="text-white font-medium">{selectedTrade.setup_name}</span>
                        </div>
                      )}
                      {selectedTrade.timeframe && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Timeframe</span>
                          <span className="text-white font-mono">{selectedTrade.timeframe}</span>
                        </div>
                      )}
                      {selectedTrade.confidence && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Confidence</span>
                          <span className={`font-mono uppercase ${
                            selectedTrade.confidence === "high" ? "text-[#00E676]" :
                            selectedTrade.confidence === "medium" ? "text-[#FFC107]" :
                            "text-[#FF3B30]"
                          }`}>
                            {selectedTrade.confidence}
                          </span>
                        </div>
                      )}
                      {selectedTrade.original_rr && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Planned RR</span>
                          <span className="text-white font-mono">{selectedTrade.original_rr?.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-white/40">Created</span>
                        <span className="text-white/60">
                          {selectedTrade.created_at && format(new Date(selectedTrade.created_at), "MMM d, yyyy HH:mm")}
                        </span>
                      </div>
                    </div>

                    {/* Notes */}
                    {selectedTrade.notes && (
                      <div className="pt-4 border-t border-white/10">
                        <div className="text-white/40 text-xs font-mono mb-2">NOTES</div>
                        <p className="text-white/80 text-sm whitespace-pre-wrap">{selectedTrade.notes}</p>
                      </div>
                    )}

                    {/* Read-only notice */}
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-white/30 text-xs text-center">
                        This is a read-only view. To edit trades, go to Trade History.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
